import React, { FC, ReactNode, useId } from 'react';
import { twMerge } from 'tailwind-merge';

import { IconProps } from './icon.types';

export enum BuildingIconVariant {
  DEFAULT,
  EVENT,
  FOUNDATION,
}

export type BuildingIconProps = { variant?: BuildingIconVariant } & IconProps;

const INCORPORATION_OUTLINE_PATH =
  'M5.23336 7.31706H2.81669C2.34998 7.31706 2.11663 7.31706 1.93837 7.40789C1.78156 7.48778 1.65408 7.61526 1.57419 7.77206C1.48336 7.95032 1.48336 8.18368 1.48336 8.65039V15.6504M12.7334 7.31706H15.15C15.6167 7.31706 15.8501 7.31706 16.0283 7.40789C16.1852 7.48778 16.3126 7.61526 16.3925 7.77206C16.4834 7.95032 16.4834 8.18368 16.4834 8.65039V15.6504M12.7334 15.6504V3.31706C12.7334 2.38364 12.7334 1.91693 12.5517 1.56041C12.3919 1.2468 12.1369 0.991835 11.8233 0.832046C11.4668 0.650391 11.0001 0.650391 10.0667 0.650391H7.90002C6.9666 0.650391 6.49989 0.650391 6.14337 0.832046C5.82977 0.991835 5.5748 1.2468 5.41501 1.56041C5.23336 1.91693 5.23336 2.38364 5.23336 3.31706V15.6504M17.3167 15.6504H0.650024M8.15002 3.98372H9.81669M8.15002 7.31706H9.81669M8.15002 10.6504H9.81669';

const INCORPORATION_FILL_PATH =
  'M2.81669 7.31706H5.23336V3.31706C5.23336 2.38364 5.23336 1.91693 5.41501 1.56041C5.5748 1.2468 5.82977 0.991835 6.14337 0.832046C6.49989 0.650391 6.9666 0.650391 7.90002 0.650391H10.0667C11.0001 0.650391 11.4668 0.650391 11.8233 0.832046C12.1369 0.991835 12.3919 1.2468 12.5517 1.56041C12.7334 1.91693 12.7334 2.38364 12.7334 3.31706V7.31706H15.15C15.6167 7.31706 15.8501 7.31706 16.0283 7.40789C16.1852 7.48778 16.3126 7.61526 16.3925 7.77206C16.4834 7.95032 16.4834 8.18368 16.4834 8.65039V15.6504H12.7334H5.23336H1.48336V8.65039C1.48336 8.18368 1.48336 7.95032 1.57419 7.77206C1.65408 7.61526 1.78156 7.48778 1.93837 7.40789C2.11663 7.31706 2.34998 7.31706 2.81669 7.31706Z';

const buildingIconVariant: Record<
  BuildingIconVariant,
  (props: { gradientId?: string } & Except<BuildingIconProps, 'variant'>) => ReactNode
> = {
  [BuildingIconVariant.DEFAULT]: ({ iconColor = '#2565C8', className, ...props }) => (
    <svg
      className={twMerge('shrink-0', className)}
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.8332 9.16667H14.8332C15.7666 9.16667 16.2333 9.16667 16.5898 9.34832C16.9034 9.50811 17.1584 9.76308 17.3182 10.0767C17.4998 10.4332 17.4998 10.8999 17.4998 11.8333V17.5M10.8332 17.5V5.16667C10.8332 4.23325 10.8332 3.76654 10.6515 3.41002C10.4917 3.09641 10.2368 2.84144 9.92315 2.68166C9.56664 2.5 9.09992 2.5 8.1665 2.5H5.1665C4.23308 2.5 3.76637 2.5 3.40985 2.68166C3.09625 2.84144 2.84128 3.09641 2.68149 3.41002C2.49984 3.76654 2.49984 4.23325 2.49984 5.16667V17.5M18.3332 17.5H1.6665M5.4165 5.83333H7.9165M5.4165 9.16667H7.9165M5.4165 12.5H7.9165"
        stroke={iconColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  ),

  [BuildingIconVariant.EVENT]: ({ iconColor = '#344054', className, ...props }) => (
    <svg
      className={twMerge('shrink-0', className)}
      fill="none"
      height="17"
      viewBox="0 0 18 17"
      width="18"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={INCORPORATION_OUTLINE_PATH}
        stroke={iconColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.3"
      />
    </svg>
  ),

  [BuildingIconVariant.FOUNDATION]: ({ className, gradientId, ...props }) => (
    <svg
      className={twMerge('shrink-0', className)}
      fill="none"
      height="17"
      viewBox="0 0 18 17"
      width="18"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={INCORPORATION_FILL_PATH} fill="#F4F8FF" />
      <path
        d={INCORPORATION_OUTLINE_PATH}
        stroke={gradientId ? `url(#${gradientId})` : '#2565C8'}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.3"
      />
      {gradientId && (
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="5.4834"
            x2="22.4834"
            y1="7.65039"
            y2="29.1504"
          >
            <stop stopColor="#2565C8" />
            <stop offset="1" stopColor="#123162" />
          </linearGradient>
        </defs>
      )}
    </svg>
  ),
};

const BuildingIcon: FC<BuildingIconProps> = ({
  variant = BuildingIconVariant.DEFAULT,
  ...props
}) => {
  const gradientId = useId();

  return buildingIconVariant[variant]({
    ...props,
    gradientId: variant === BuildingIconVariant.FOUNDATION ? gradientId : undefined,
  });
};

export default BuildingIcon;
